import { Ticket } from "@/lib/redux/features/booking/type";

export type PnrDisplayStatus = "success" | "failed" | "unknown" | "hold";

export type FlightPnrDisplayItem = {
  pnr: string;
  airline?: string;
  status: PnrDisplayStatus;
};

const AIRLINE_DISPLAY_MAP: Record<string, string> = {
  "_9G": "SUN PQ"
};

const getDisplayAirline = (airline?: string): string => {
  const normalizedAirline = (airline || "").trim().toUpperCase();
  if (!normalizedAirline) return "";
  return AIRLINE_DISPLAY_MAP[normalizedAirline] || normalizedAirline;
};

export const formatFlightPnrDisplay = (item: Pick<FlightPnrDisplayItem, "pnr" | "airline">): string => {
  const airline = getDisplayAirline(item.airline);
  const pnr = (item.pnr || "").trim();
  return airline ? `${airline} - ${pnr}` : pnr;
};

type GetFlightPnrDisplayItemsInput = {
  pnrNumber?: string;
  tickets?: Ticket[];
  orderStatus?: string;
  bookingStatus?: string;
  ticketStatus?: string;
};

const SUCCESS_STATUSES = ["ACTIVE", "CONFIRM", "ISSUED"];
const FAILED_STATUSES = ["FAIL", "FAILED", "CANCEL", "CANCELLED", "ERROR"];

const getStatusRank = (status: PnrDisplayStatus): number => {
  if (status === "failed") return 3;
  if (status === "success") return 2;
  return 1;
};

const normalizeStatus = (value?: string): string => (value || "").toUpperCase().trim();

const parseStatus = (...statuses: Array<string | undefined>): PnrDisplayStatus => {
  const normalizedStatuses = statuses.map(normalizeStatus).filter(Boolean);
  if (normalizedStatuses.some((status) => FAILED_STATUSES.includes(status))) {
    return "failed";
  }
  if (normalizedStatuses.some((status) => SUCCESS_STATUSES.includes(status))) {
    return "success";
  }
  if (normalizedStatuses.includes("HOLD")) {
    return "hold";
  }
  return "unknown";
};

const resolveTicketAirline = (ticket: Ticket): string | undefined => {
  const airline =
    ticket?.airline ||
    ticket?.metaData?.airline ||
    ticket?.bookingDetails?.bookings?.[0]?.airline;
  const normalized = (airline || "").trim();
  return normalized || undefined;
};

export const getFlightPnrDisplayItems = ({
  pnrNumber,
  tickets = [],
  orderStatus,
  bookingStatus,
  ticketStatus
}: GetFlightPnrDisplayItemsInput): FlightPnrDisplayItem[] => {
  const orderLevelStatus = parseStatus(bookingStatus, ticketStatus, orderStatus);
  const map = new Map<string, FlightPnrDisplayItem>();

  tickets.forEach((ticket) => {
    const pnr = (ticket?.pnrNumber || "").trim();
    if (!pnr) return;

    const ticketLevelStatus = parseStatus(ticket?.bookingStatus, ticket?.ticketStatus);
    const nextItem: FlightPnrDisplayItem = {
      pnr,
      airline: resolveTicketAirline(ticket),
      status: ticketLevelStatus === "unknown" ? orderLevelStatus : ticketLevelStatus
    };

    const existing = map.get(pnr);
    if (!existing) {
      map.set(pnr, nextItem);
      return;
    }

    const merged: FlightPnrDisplayItem = {
      pnr,
      airline: existing.airline || nextItem.airline,
      status: getStatusRank(nextItem.status) > getStatusRank(existing.status) ? nextItem.status : existing.status
    };
    map.set(pnr, merged);
  });

  (pnrNumber || "")
    .split(";")
    .map((value) => value.trim())
    .filter(Boolean)
    .forEach((pnr) => {
      if (!map.has(pnr)) {
        map.set(pnr, { pnr, status: orderLevelStatus });
      }
    });

  return Array.from(map.values());
};

export const getPnrStatusTagColor = (
  status: PnrDisplayStatus,
): "error" | "success" | "warning" | "default" => {
  if (status === "failed") return "error";
  if (status === "success") return "success";
  if (status === "hold") return "warning";
  return "default";
};

export const getIsPartialSuccess = (items: FlightPnrDisplayItem[]): boolean => {
  const hasSuccess = items.some((item) => item.status === "success");
  const hasFailed = items.some((item) => item.status === "failed");
  return hasSuccess && hasFailed;
};
